import React, { useState, useRef, useEffect } from 'react';
import { aiAdvisor } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Bot, Send, User, Sparkles, TrendingUp, DollarSign, 
  PieChart, Loader2, MessageSquarePlus, Lightbulb,
  ArrowDown, RotateCcw
} from 'lucide-react';

const SUGERENCIAS = [
  { text: '¿Cuál es mi estado financiero actual?', icon: TrendingUp, color: 'text-emerald-600' },
  { text: '¿Cuál es mi gasto más alto este mes?', icon: DollarSign, color: 'text-amber-600' },
  { text: '¿Qué cliente es más rentable?', icon: PieChart, color: 'text-blue-600' },
  { text: 'Dame un resumen de mis finanzas', icon: Sparkles, color: 'text-purple-600' },
];

const AIAdvisor = () => {
  const [mensajes, setMensajes] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [mensajes]);

  // Handle scroll to show/hide scroll button
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [inputValue]);

  const handleSend = async (pregunta = inputValue) => {
    if (!pregunta.trim()) return;
    
    const userMessage = {
      tipo: 'user',
      texto: pregunta,
      timestamp: new Date()
    };
    setMensajes(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await aiAdvisor.chat(pregunta);
      
      const botMessage = {
        tipo: 'bot',
        texto: response.data.respuesta,
        contexto: response.data.contexto,
        timestamp: new Date()
      };
      setMensajes(prev => [...prev, botMessage]);
    } catch (error) {
      toast.error('Error al procesar tu pregunta');
      const errorMessage = {
        tipo: 'bot',
        texto: 'Lo siento, hubo un error al procesar tu pregunta. Por favor intenta de nuevo.',
        timestamp: new Date(),
        isError: true
      };
      setMensajes(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setMensajes([]);
    setInputValue('');
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isEmptyChat = mensajes.length === 0;

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] bg-gradient-to-b from-slate-50 to-white" data-testid="ai-advisor-page">
      {/* Header - Minimal */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">AI Advisor</h1>
            <p className="text-xs text-slate-500">Asesor financiero inteligente</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleNewChat}
          className="gap-2 text-slate-600 hover:text-slate-800"
        >
          <RotateCcw className="w-4 h-4" />
          Nueva conversación
        </Button>
      </div>

      {/* Main Chat Area */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto relative"
      >
        {isEmptyChat ? (
          /* Empty State - Welcome Screen */
          <div className="flex flex-col items-center justify-center h-full px-4 py-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-2xl shadow-primary/30 mb-6">
              <Bot className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">¡Hola! Soy tu AI Advisor</h2>
            <p className="text-slate-500 text-center max-w-md mb-8">
              Puedo ayudarte a analizar tus datos financieros, identificar tendencias y responder preguntas sobre tu negocio.
            </p>
            
            {/* Sugerencias Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl">
              {SUGERENCIAS.map((sug, index) => (
                <button
                  key={index}
                  onClick={() => handleSend(sug.text)}
                  disabled={loading}
                  className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-primary/50 hover:shadow-md transition-all text-left group"
                >
                  <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center group-hover:bg-primary/10 transition-colors ${sug.color}`}>
                    <sug.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700 group-hover:text-slate-900">{sug.text}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Capacidades */}
            <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 max-w-2xl w-full">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">¿Qué puedo hacer?</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-blue-700">
                <span>• Análisis de gastos</span>
                <span>• Resumen financiero</span>
                <span>• Identificar tendencias</span>
                <span>• Recomendaciones</span>
              </div>
            </div>
          </div>
        ) : (
          /* Messages List */
          <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
            {mensajes.map((mensaje, index) => (
              <div 
                key={index}
                className={`flex gap-4 ${mensaje.tipo === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {mensaje.tipo === 'bot' && (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center flex-shrink-0 shadow-md">
                    <Bot className="w-5 h-5 text-white" />
                  </div>
                )}
                
                <div className={`max-w-[75%] ${mensaje.tipo === 'user' ? 'order-first' : ''}`}>
                  <div 
                    className={`px-4 py-3 rounded-2xl ${
                      mensaje.tipo === 'user' 
                        ? 'bg-primary text-white rounded-br-md shadow-lg shadow-primary/20' 
                        : mensaje.isError 
                          ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
                          : 'bg-white border border-slate-200 rounded-bl-md shadow-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{mensaje.texto}</p>
                  </div>
                  <p className={`text-xs text-slate-400 mt-1.5 ${mensaje.tipo === 'user' ? 'text-right' : ''}`}>
                    {formatTime(mensaje.timestamp)}
                  </p>
                </div>
                
                {mensaje.tipo === 'user' && (
                  <div className="w-9 h-9 rounded-xl bg-slate-700 flex items-center justify-center flex-shrink-0 shadow-md">
                    <User className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex gap-4 justify-start">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-white border border-slate-200 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                    <span className="text-sm text-slate-500">Analizando tus datos...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors"
          >
            <ArrowDown className="w-5 h-5 text-slate-600" />
          </button>
        )}
      </div>

      {/* Input Area - Fixed at bottom */}
      <div className="border-t bg-white/80 backdrop-blur-sm px-4 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex gap-3 items-end bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/20 transition-all">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu pregunta sobre tus finanzas..."
              disabled={loading}
              rows={1}
              className="flex-1 resize-none border-0 bg-transparent focus:outline-none text-sm text-slate-700 placeholder:text-slate-400 max-h-36"
              style={{ minHeight: '24px' }}
              data-testid="chat-input"
            />
            <Button 
              onClick={() => handleSend()}
              disabled={loading || !inputValue.trim()}
              size="sm"
              className="rounded-xl px-4 h-9 gap-2 shadow-md"
              data-testid="send-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">Enviar</span>
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">
            Presiona Enter para enviar • Shift + Enter para nueva línea
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAdvisor;
